import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import LogoPrefeitura from "../../assets/images/logo-prefeitura.png";
import { loginEmpresa } from "../../services/auth";
import { useAuth } from "../../contexts/AuthContext";
import { recuperarSenhaEmpresa } from "../../services/empresa";
import {
  aplicarMascaraCpfCnpj,
  validarFormatoCpfCnpj,
  detectarTipoCpfCnpj,
} from "../../utils/normalization";

interface LoginForm {
  cnpj: string;
  senha: string;
}

const LoginEmpresa: React.FC = () => {
  const navigate = useNavigate();
  const { login, isLoading, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState<LoginForm>({
    cnpj: "",
    senha: "",
  });

  // Only clear authentication data if explicitly coming from a logout - wait for AuthContext to initialize
  useEffect(() => {
    if (isLoading) return; // Wait for AuthContext to finish initialization

    const urlParams = new URLSearchParams(window.location.search);
    const shouldClearAuth = urlParams.get("logout") === "true";

    if (shouldClearAuth) {
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      sessionStorage.removeItem("authToken");
      sessionStorage.removeItem("refreshToken");

      localStorage.removeItem("empresaId");
      localStorage.removeItem("empresaNome");
      localStorage.removeItem("empresaEmail");
      localStorage.removeItem("empresaCnpj");
      localStorage.removeItem("empresaTelefone");
      sessionStorage.removeItem("empresaId");
      sessionStorage.removeItem("empresaNome");
      sessionStorage.removeItem("empresaEmail");
      sessionStorage.removeItem("empresaCnpj");
      sessionStorage.removeItem("empresaTelefone");

      window.history.replaceState({}, "", window.location.pathname);
    } else {
      console.log("[LoginEmpresa] Preservando tokens existentes...");
    }
  }, [isLoading]);

  // Redireciona ao dashboard apenas quem já tem sessão DE EMPRESA. Antes qualquer
  // sessão (ex.: prefeitura) caía no dashboard da empresa, que ficava zerado.
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const userType = localStorage.getItem("userType");
      if (userType === "empresa") {
        navigate("/dashboard-empresa");
      }
    }
  }, [isLoading, isAuthenticated, navigate]);

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [recoveryModal, setRecoveryModal] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryMessage, setRecoveryMessage] = useState("");

  const handleCNPJChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = aplicarMascaraCpfCnpj(e.target.value);
    setFormData((prev) => ({
      ...prev,
      cnpj: formatted,
    }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      senha: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!validarFormatoCpfCnpj(formData.cnpj)) {
      const tipo = detectarTipoCpfCnpj(formData.cnpj);
      if (tipo === "INVALIDO") {
        setError(
          "CPF/CNPJ inválido. Digite um CPF (11 dígitos) ou CNPJ (14 dígitos) válido.",
        );
      }
      return;
    }

    if (!formData.senha) {
      setError("Por favor, digite sua senha.");
      return;
    }
    setLoading(true);

    try {
      const response = await loginEmpresa(formData);
      const { accessToken, user } = response.data;

      const userData = {
        id: parseInt(user.id.toString()),
        nome: user.nome,
        email: user.email,
        tipo: "empresa" as const,
        cnpj: user.cnpj,
        telefone: user.telefone,
      };

      login(accessToken, userData);

      setLoading(false);

      setTimeout(() => {
        navigate("/dashboard-empresa");
      }, 500);
    } catch (error: any) {
      setLoading(false);

      // Tratar diferentes tipos de erro
      if (error.code === "ERR_NETWORK") {
        setError(
          "Erro de conexão: Backend não está acessível. Verifique se o servidor está rodando na porta 8093.",
        );
      } else if (error.response) {
        // Erro da API
        if (error.response.status === 401) {
          setError("CPF/CNPJ ou senha incorretos. Verifique suas credenciais.");
        } else if (error.response.status === 403) {
          setError(
            error.response.data?.message ||
              "Empresa não habilitada. Entre em contato com a prefeitura.",
          );
        } else if (error.response.status === 404) {
          setError("Empresa não encontrada. Verifique o CPF/CNPJ digitado.");
        } else if (error.response.status === 500) {
          setError("Erro no servidor. Tente novamente mais tarde.");
        } else {
          setError(
            error.response.data?.message ||
              "Erro ao fazer login. Tente novamente.",
          );
        }
      } else if (error.request) {
        // Requisição feita mas sem resposta
        setError(
          "Sem resposta do servidor. Verifique sua conexão e se o backend está rodando.",
        );
      } else {
        // Outro tipo de erro
        setError(error.message || "Erro inesperado. Tente novamente.");
      }
    }
  };

  const handleRecoveryCancel = () => {
    setRecoveryModal(false);
    setRecoveryEmail("");
    setRecoveryMessage("");
  };

  const handleRecoverySubmit = async () => {
    if (!recoveryEmail) {
      setRecoveryMessage("Por favor, digite um email.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recoveryEmail)) {
      setRecoveryMessage("Por favor, digite um email válido.");
      return;
    }

    setRecoveryLoading(true);
    setRecoveryMessage("");

    try {
      const response = await recuperarSenhaEmpresa(recoveryEmail);

      if (response.success) {
        setRecoveryMessage(
          "Instruções de recuperação enviadas para o email informado."
        );
        setTimeout(() => {
          handleRecoveryCancel();
        }, 3000);
      } else {
        setRecoveryMessage(
          response.message || "Erro ao enviar email de recuperação. Tente novamente."
        );
      }
    } catch (error) {
      setRecoveryMessage(
        "Erro ao conectar com o servidor. Tente novamente mais tarde."
      );
    } finally {
      setRecoveryLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f5f5",
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 5,
            pt: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRadius: 2,
            backgroundColor: "#ffffff",
            maxWidth: 450,
            margin: "0 auto",
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src={LogoPrefeitura}
            alt="Logo Prefeitura"
            sx={{
              width: 240,
              height: "auto",
              mb: 3,
              objectFit: "contain",
            }}
          />

          {/* Título */}
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 700, mb: 0.5, color: "#333" }}
          >
            Portal de Vagas
          </Typography>

          <Typography variant="body1" sx={{ mb: 2, color: "#666" }}>
            Prefeitura de Bom Retiro do Sul
          </Typography>

          {/* Formulário */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              width: "100%",
              mt: 1,
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="CPF/CNPJ"
              value={formData.cnpj}
              onChange={handleCNPJChange}
              margin="normal"
              required
              placeholder="000.000.000-00 ou 00.000.000/0000-00"
              variant="outlined"
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#ffffff",
                  borderRadius: 2,
                  "& fieldset": {
                    borderColor: "#e0e0e0",
                    borderWidth: 1,
                  },
                  "&:hover fieldset": {
                    borderColor: "#bdbdbd",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1976d2",
                    borderWidth: 2,
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#666",
                  fontSize: "0.875rem",
                  "&.Mui-focused": {
                    color: "#1976d2",
                  },
                },
                "& .MuiOutlinedInput-input": {
                  padding: "16px 14px",
                  color: "#333",
                  "&::placeholder": {
                    color: "#999",
                    opacity: 1,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon sx={{ color: "#666", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              inputProps={{
                maxLength: 18,
              }}
            />

            <TextField
              fullWidth
              label="Senha"
              type={showPassword ? "text" : "password"}
              value={formData.senha}
              onChange={handlePasswordChange}
              margin="normal"
              required
              variant="outlined"
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#ffffff",
                  borderRadius: 2,
                  "& fieldset": {
                    borderColor: "#e0e0e0",
                    borderWidth: 1,
                  },
                  "&:hover fieldset": {
                    borderColor: "#bdbdbd",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1976d2",
                    borderWidth: 2,
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#666",
                  fontSize: "0.875rem",
                  "&.Mui-focused": {
                    color: "#1976d2",
                  },
                },
                "& .MuiOutlinedInput-input": {
                  padding: "16px 14px",
                  color: "#333",
                  "&::placeholder": {
                    color: "#999",
                    opacity: 1,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: "#666", fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      sx={{ color: "#666" }}
                    >
                      {showPassword ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ textAlign: "right", mt: 1, mb: 2 }}>
              <Button
                variant="text"
                onClick={() => setRecoveryModal(true)}
                sx={{
                  textTransform: "none",
                  color: "#1976d2",
                  fontWeight: 400,
                  fontSize: "0.875rem",
                  padding: 0,
                  minWidth: 'auto',
                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                Esqueceu sua senha?
              </Button>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mb: 1.5,
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
                borderRadius: 2,
                backgroundColor: "#2196f3",
                boxShadow: "none",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1976d2",
                  boxShadow: "none",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Entrar"
              )}
            </Button>

            <Box sx={{ textAlign: "center", mt: 0.5 }}>
              <Typography variant="body2" sx={{ color: "#666" }}>
                Não possui cadastro?{" "}
                <Button
                  variant="text"
                  onClick={() => navigate("/cadastrar-empresa")}
                  sx={{
                    textTransform: "none",
                    color: "#1976d2",
                    fontWeight: 500,
                    "&:hover": {
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  Cadastre sua empresa
                </Button>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* Modal de Recuperação de Senha */}
      <Dialog
        open={recoveryModal}
        onClose={handleRecoveryCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 1, backgroundColor: "#ffffff" }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
            Recuperar Senha
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2, backgroundColor: "#ffffff" }}>
          <Typography
            variant="body2"
            sx={{ mb: 3, color: "#666", textAlign: "center" }}
          >
            Digite o email cadastrado da sua empresa para receber as instruções
            de recuperação de senha.
          </Typography>

          {recoveryMessage && (
            <Alert
              severity={
                recoveryMessage.includes("enviadas") ? "success" : "error"
              }
              sx={{ mb: 2 }}
            >
              {recoveryMessage}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Email da empresa"
            type="email"
            value={recoveryEmail}
            onChange={(e) => setRecoveryEmail(e.target.value)}
            variant="outlined"
            disabled={recoveryLoading}
            placeholder="email@empresa.com"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#ffffff",
                borderRadius: 2,
                "& fieldset": {
                  borderColor: "#e0e0e0",
                  borderWidth: 1,
                },
                "&:hover fieldset": {
                  borderColor: "#bdbdbd",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#1976d2",
                  borderWidth: 2,
                },
              },
              "& .MuiInputLabel-root": {
                color: "#666",
                "&.Mui-focused": {
                  color: "#1976d2",
                },
              },
              "& .MuiOutlinedInput-input": {
                padding: "16px 14px",
                color: "#333",
                "&::placeholder": {
                  color: "#999",
                  opacity: 1,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: "#666", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1, backgroundColor: "#ffffff" }}>
          <Button
            onClick={handleRecoveryCancel}
            variant="outlined"
            disabled={recoveryLoading}
            sx={{
              textTransform: "none",
              color: "#666",
              borderColor: "#e0e0e0",
              "&:hover": {
                borderColor: "#bdbdbd",
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleRecoverySubmit}
            variant="contained"
            disabled={recoveryLoading || !recoveryEmail}
            sx={{
              textTransform: "none",
              backgroundColor: "#1976d2",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            {recoveryLoading ? <CircularProgress size={24} color="inherit" /> : "Enviar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoginEmpresa;
